import { BasketResponse, PackageAvailabilityResponse, PackagesResponse } from '@model/iceberg';
import { ISO_8601_DATE_FORMAT } from '@model/common/dates';
import { Guests, Rooms } from '@model/search';
import moment from 'moment';
import uri from 'urijs';
import {
  AvailabilityParams,
  FlightsAvailabilityResponse,
  OccupancyParam,
  SeatMapsResponse,
  PutSeatMapsParams
} from '@model/iceberg/service';
import { TourRoomsResponse } from '@model/iceberg/deal-finder/deal-finder';
import { EndPoint, getURL, Request } from '@model/service';
import { BookingEndpoints } from '@model/service/booking-endpoints';

interface BookingApiRequests {
  get: (token: string) => Promise<BasketResponse>;
  delete: (token: string) => Promise<BasketResponse>;
  getPackageAvailability: (searchToken: string, params: AvailabilityParams) => Promise<PackageAvailabilityResponse>;
  getFlights: (token: string) => Promise<FlightsAvailabilityResponse>;
  getExtras: (token: string) => Promise<any>;
  getExtrasOptions: (token: string) => Promise<any>;
  putExtrasOptions: (payload: PutExtrasOptionsParams) => Promise<any>;
  fetchSeatMaps: (token: string) => Promise<SeatMapsResponse>;
  putSeatMaps: (payload: PutSeatMapsParams) => Promise<SeatMapsResponse>;
}

export interface PromoCodeResponse {
  data: PromoCode;
}

export interface PromoCode {
  promoCode: string;
  valid: boolean;
  error?: string;
  discount?: number;
}

export interface TokenResponse {
  searchToken: string;
  expires: string;
}

export interface SearchPayload {
  path: string;
  from: Array<string>;
  display: string;
  startDate: string;
  endDate: string;
  occupancy: Array<OccupancyParam>;
  preferredBoardBasis: string;
}

export interface FilterOption {
  name: string;
  value: any;
  selected: boolean | number;
  count: number;
}

export interface Filter {
  name: string;
  type: FilterType;
  formattedName: string;
  values: Array<FilterOption>;
}

export enum FilterType {
  NONE = '',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  STARS = 'stars',
  TOGGLE = 'toggle',
  PRICE = 'price',
  SORT = 'sort'
}

export enum SortType {
  POPULARITY = 'popularity',
  LOWEST = 'lowest',
  HIGHEST = 'highest',
  TRIPADVISOR = 'tripadvisor',
  ATOZ = 'atoz',
  ZTOA = 'ztoa',
  LEAST_SELLER = 'zpopularity',
  TOUR_LENGTH_LONG = 'tourlong',
  TOUR_LENGTH_SHORT = 'tourshort'
}

export const DEFAULT_SORT_TYPE: SortType = SortType.POPULARITY;

export interface DataEnvelope<T> {
  data: T;
}

export interface PutExtrasOptionsParams {
  searchToken: string;
  token: string;
  optionToken: string;
}

export interface GetTourRoomsParams {
  searchToken: string;
}

export interface TourRoomSelection {
  connectId: number;
  sequence: string;
  roomToken: string;
  hotelToken: string;
}

export interface PutTourRoomsParams {
  searchToken: string;
  rooms: Array<TourRoomSelection>;
}

export class BookingApi implements BookingApiRequests {
  private api: Request = new Request();

  public async get(token: string, preBook: boolean = false): Promise<BasketResponse> {
    return await this.api.get(
      uri(getURL()).path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.BASKET}`).query({ preBook }).href()
    );
  }

  public async delete(token: string): Promise<BasketResponse> {
    return await this.api.delete(uri(getURL()).path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.BASKET}`).href());
  }

  public async promoCode(token: string, promoCode: string): Promise<PromoCodeResponse> {
    return await this.api.get(
      uri(getURL())
        .path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.BASKET}${BookingEndpoints.PROMO_CODE}/${promoCode}`)
        .href()
    );
  }

  public async getPackageAvailability(token: string, params: AvailabilityParams): Promise<PackageAvailabilityResponse> {
    const occupancy: Array<OccupancyParam> = this.getOccupancy(params.occupancy);
    const url = uri(getURL()).path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.PACKAGES}`).href();
    return await this.api.post(url, { ...params, searchToken: token, occupancy });
  }

  public async getToken(payload: SearchPayload): Promise<TokenResponse> {
    return await this.api.post(uri(getURL()).path(EndPoint.BOOKING.concat(EndPoint.PACKAGE)).href(), payload);
  }

  public async packages(token: string, query: Record<string, string>): Promise<DataEnvelope<PackagesResponse>> {
    return await this.api.get(
      uri(getURL())
        .path(EndPoint.BOOKING.concat(`/${token}`).concat(EndPoint.PACKAGE))
        .search(query)
        .href()
    );
  }

  public async getSinglePackage(token: string, accommodationToken: string): Promise<any> {
    return await this.api.get(
      uri(getURL()).path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.PACKAGES}/${accommodationToken}`).href()
    );
  }

  public async getFlights(token: string): Promise<FlightsAvailabilityResponse> {
    const url = uri(getURL()).path(`${EndPoint.DEAL_FINDER}/${token}${BookingEndpoints.FLIGHTS}`).href();
    return await this.api.get(url);
  }

  public async getExtras(token: string): Promise<any> {
    const url = uri(getURL()).path(`${EndPoint.BOOKING}/${token}${BookingEndpoints.EXTRAS}`).href();
    return await this.api.get(url);
  }

  public async getExtrasOptions(token: string): Promise<any> {
    const url = uri(getURL())
      .path(`${EndPoint.DEAL_FINDER}/${token}${BookingEndpoints.EXTRAS}${BookingEndpoints.OPTIONS}`)
      .href();
    return await this.api.get(url);
  }

  public async putExtrasOptions({ searchToken, token, optionToken }: PutExtrasOptionsParams): Promise<any> {
    const url = uri(getURL())
      .path(`${EndPoint.DEAL_FINDER}/${searchToken}${BookingEndpoints.EXTRAS}${BookingEndpoints.OPTION}`)
      .href();
    return await this.api.put(url, { extra: { token, optionToken } });
  }

  private getOccupancy(rooms: Rooms): Array<OccupancyParam> {
    return rooms.map((guests: Guests) => ({
      adults: guests.adults,
      children: guests.children.map(({ year, month, day }) =>
        moment(`${year}-${month}-${day}`, ISO_8601_DATE_FORMAT).format(ISO_8601_DATE_FORMAT)
      )
    }));
  }

  public async fetchSeatMaps(searchToken: string): Promise<SeatMapsResponse> {
    const query: string = uri(getURL())
      .path(EndPoint.DEAL_FINDER.concat('/').concat(searchToken).concat(BookingEndpoints.SEAT_MAPS))
      .href();
    return await this.api.get(query);
  }

  public async putSeatMaps({ searchToken, guestMaps }: PutSeatMapsParams): Promise<SeatMapsResponse> {
    const url = uri(getURL())
      .path(`${EndPoint.DEAL_FINDER}/${searchToken}${BookingEndpoints.SEAT_MAPS}${BookingEndpoints.UPDATE}`)
      .href();
    return await this.api.put(url, { guestMaps });
  }

  public async getTourRooms({ searchToken }: GetTourRoomsParams): Promise<DataEnvelope<Array<TourRoomsResponse>>> {
    const url = uri(getURL()).path(`${EndPoint.DEAL_FINDER}/${searchToken}${BookingEndpoints.TOUR_ROOMS}`).href();
    return await this.api.get(url);
  }

  public async setTourRooms({ searchToken, rooms }: PutTourRoomsParams): Promise<any> {
    const url = uri(getURL()).path(`${EndPoint.DEAL_FINDER}/${searchToken}${BookingEndpoints.TOUR_ROOMS}`).href();
    return await this.api.put(url, { rooms });
  }
}
